import { Component, OnInit, ElementRef, ViewChild } from "@angular/core"
import { Router, ActivatedRoute } from "@angular/router"
import { Location } from "@angular/common"
import { ApiService } from "../../services/api.service"
import { ModalService } from "../../services/modal.service"
import { PlayerService } from "../../services/audio/player.service"
import { BillingService } from "../../services/billing.service"
import { TokenService } from "../../services/token.service"
import { environment } from "../../../environments/environment"
import { UserService } from "../../services/user.service"
import { FolderService } from "../../services/folder.service"
import { DesignService } from "../../services/design.service"
import { DeviceDetectorService } from "ngx-device-detector"
import { GeneralService } from "@services/general/general.service"
import { WindowService } from "@services/window.service"
import { MenuModifiers } from "../reusable/menu-modifiers/menu-modifiers.component"
import { CreateTrackFrom } from "@common-lib/types/general"
import { featureFlags } from "@common-lib/utils/feature-flags"
import { Helpers } from "../../modules/helpers.module"
import { AudioService } from "@services/audio/audio.service"
import { SupportChatService } from "@services/supportchat.service"
import { LoginHandoffService } from "@services/login-handoff.service"
import {
    MenuOptions,
    MenuOptionsComponent,
} from "../reusable/menu-options/menu-options.component"

@Component({
    selector: "app-navbar",
    templateUrl: "navbar.component.html",
    styleUrls: ["navbar.component.scss"],
})
export class NavbarComponent implements OnInit {
    scrollConfig = { useBothWheelAxes: false, suppressScrollX: true }

    email = ""
    locationPath = ""
    userMenuOpen = false
    userMenuOptions: MenuOptions<any> | undefined
    userMenuWidth: number
    unplayedComposition = false
    mobileSettingsMenuOpen = false
    mobileSettingsMenuOptions: MenuOptions<null> | undefined

    isLoggedIn = false
    userCanChangePassword = true

    isElectron = false

    songGenerationQuota = null

    isMacElectronApp = false

    isCollapsed = false
    isDesktop = false

    showCommunity = false

    @ViewChild("feedbackForm", { static: false }) feedbackFormField: ElementRef
    @ViewChild("userMenuButton", { static: false })
    userMenuButton: ElementRef<HTMLElement>
    @ViewChild(MenuOptionsComponent, { static: false })
    userMenuComponent: MenuOptionsComponent

    constructor(
        private windowService: WindowService,
        protected route: ActivatedRoute,
        private folderService: FolderService,
        protected billingService: BillingService,
        protected tokenService: TokenService,
        protected modalService: ModalService,
        private router: Router,
        private apiService: ApiService,
        protected location: Location,
        private generalService: GeneralService,
        private userService: UserService,
        private designService: DesignService,
        private device: DeviceDetectorService,
        private supportChatService: SupportChatService,
        private loginHandoffService: LoginHandoffService,
        public audio: AudioService
    ) {
        this.isDesktop = this.device.isDesktop()
    }

    detectChanges() {
        //this.ref.detectChanges()
    }

    ngOnInit() {
        this.showCommunity = this.folderService.content.getValue().length > 0

        this.isElectron = this.windowService.desktopAppAPI != null

        if (this.isElectron) {
            this.windowService.generalAPI.getMetaInfo().then(result => {
                this.isMacElectronApp =
                    result["platform"] == "darwin" ||
                    result["platform"] == "mac"
            })
        }

        this.folderService.content.subscribe(compositions => {
            if (compositions == null) {
                return
            }

            for (var c = 0; c < compositions.length; c++) {
                if (
                    compositions[c].isFinished &&
                    !compositions[c].failed &&
                    compositions[c].wasPlayed == false
                ) {
                    if (!this.unplayedComposition) {
                        this.unplayedComposition = true

                        this.detectChanges()
                    }

                    return
                }
            }

            if (this.unplayedComposition) {
                this.unplayedComposition = false

                this.detectChanges()
            }
        })

        this.apiService.user.subscribe(user => {
            if (user != null) {
                this.email = user
                this.userCanChangePassword =
                    this.userService.userCanChangePassword()
            } else {
                this.userMenuOpen = false
                this.userCanChangePassword = false
            }

            this.detectChanges()
        })

        this.apiService.isLoggedIn.subscribe(value => {
            this.isLoggedIn = value

            this.detectChanges()
        })

        this.router.events.subscribe(val => {
            if (this.router.url.split("?")[0].includes("/publicPlayer")) {
                this.userMenuOpen = false
                this.isLoggedIn = false
            }

            this.locationPath = this.location.path().split("?")[0]

            this.detectChanges()
        })

        this.generalService.songGenerationQuota.subscribe(value => {
            this.songGenerationQuota = value

            this.detectChanges()
        })

        this.locationPath = this.location.path().split("?")[0]

        if (this.locationPath === "/publicPlayer") {
            this.userMenuOpen = false
            this.isLoggedIn = false

            this.detectChanges()
        }

        this.designService.sidebarCollapsed.subscribe(isCollapsed => {
            this.isCollapsed = isCollapsed
            this.designService.getCSSProperty("melody-track-color")
        })
    }

    getDailyGenerationsUsed() {
        if (this.songGenerationQuota == null) {
            return null
        }

        return (
            this.songGenerationQuota.dailyGenerationsQuota -
            this.songGenerationQuota.dailyGenerationsLeft
        )
    }

    getDailyGenerationsLeft() {
        return this.songGenerationQuota?.dailyGenerationsLeft ?? null
    }

    getDailyGenerationsQuota() {
        return this.songGenerationQuota?.dailyGenerationsQuota ?? null
    }

    getHourlyGenerationsLeft() {
        return this.songGenerationQuota?.hourlyGenerationsLeft ?? null
    }

    getHourlyGenerationsQuota() {
        return this.songGenerationQuota?.hourlyGenerationsQuota ?? null
    }

    getHourlyGenerationsUsed() {
        if (
            this.getHourlyGenerationsQuota() === null ||
            this.getHourlyGenerationsLeft() === null
        ) {
            return null
        }

        return this.getHourlyGenerationsQuota() - this.getHourlyGenerationsLeft()
    }

    getGenerationQuotaTooltip() {
        const lines: string[] = []

        if (this.getHourlyGenerationsQuota() !== null) {
            lines.push("<b>Hourly</b>")
            lines.push(
                `${this.getHourlyGenerationsUsed()}/${this.getHourlyGenerationsQuota()} generations used`
            )
            lines.push(
                `Resets at ${this.formatLocalResetTime(
                    this.getNextUtcHourReset()
                )}`
            )
            lines.push("")
        }

        lines.push("<b>Daily</b>")
        lines.push(
            `${this.getDailyGenerationsUsed()}/${this.getDailyGenerationsQuota()} generations used`
        )
        lines.push(
            `Resets ${this.formatDailyResetPhrase(this.getNextUtcDayReset())}`
        )

        return lines.join("<br>")
    }

    getGenerationQuotaTooltipDisabled() {
        return this.getDailyGenerationsLeft() === null
    }

    private getNextUtcHourReset(now = new Date()): Date {
        return new Date(
            Date.UTC(
                now.getUTCFullYear(),
                now.getUTCMonth(),
                now.getUTCDate(),
                now.getUTCHours() + 1
            )
        )
    }

    private getNextUtcDayReset(now = new Date()): Date {
        return new Date(
            Date.UTC(
                now.getUTCFullYear(),
                now.getUTCMonth(),
                now.getUTCDate() + 1
            )
        )
    }

    private formatLocalResetTime(date: Date): string {
        return date.toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
        })
    }

    private formatDailyResetPhrase(date: Date, now = new Date()): string {
        const time = this.formatLocalResetTime(date)
        const resetsTomorrow =
            date.getFullYear() !== now.getFullYear() ||
            date.getMonth() !== now.getMonth() ||
            date.getDate() !== now.getDate()

        return resetsTomorrow ? `tomorrow at ${time}` : `at ${time}`
    }

    goToDiscord() {
        window.open("https://discord.gg/ypDnFXN", "_blank")
    }

    billing(): void {
        this.router.navigate(["billing"])
    }

    tutorials(): void {
        this.router.navigate(["tutorials"])
    }

    radio(): void {
        this.router.navigate(["radio"])
    }

    showRadio() {
        return 1617738203000 < Date.now()
    }

    playlists() {
        this.router.navigate(["/"])
    }

    getFeatureFlags() {
        return featureFlags
    }

    showPlaylists() {
        return true
    }

    settings(): void {
        this.modalService.modals.settings.next({
            type: "about",
        })
    }

    async myTracks() {
        if (
            this.router.url === "/radio" ||
            (this.router.url.includes("generation-profile-editor") &&
                this.folderService.getContentType() === "Compositions")
        ) {
            await this.folderService.setContentType(
                "myTracks",
                "Compositions",
                "",
                true,
                null
            )
        } else {
            this.router.navigate([""])
        }
    }

    createNewTrack(): void {
        this.router.navigate(["create"])
    }

    toggleMobileSettingsMenu(): void {
        if (this.mobileSettingsMenuOpen) {
            this.closeMobileSettingsMenu()
            return
        }

        this.mobileSettingsMenuOptions = {
            coordinates: { x: 0, y: 0 },
            options: [
                {
                    data: null,
                    icon: "assets/img/navbar/billing.svg",
                    text: "Billing",
                    loading: false,
                    enabled: true,
                    onClick: () => this.billing(),
                },
                {
                    data: null,
                    icon: "assets/img/menu/support.svg",
                    text: "Support chat",
                    loading: false,
                    enabled: true,
                    onClick: () => this.openSupportChat(),
                },
                {
                    data: null,
                    icon: "assets/img/navbar/switch.svg",
                    text: "Use AIVA 1.0",
                    loading: false,
                    enabled: true,
                    onClick: () => this.openLegacyAiva(),
                },
                {
                    data: null,
                    icon: "assets/img/menu/logout.svg",
                    text: "Logout",
                    loading: false,
                    enabled: true,
                    onClick: () => this.logout(),
                },
            ],
            onClose: () => this.closeMobileSettingsMenu(),
        }
        this.mobileSettingsMenuOpen = true
    }

    closeMobileSettingsMenu(): void {
        this.mobileSettingsMenuOpen = false
        this.mobileSettingsMenuOptions = undefined
    }

    async openLegacyAiva(): Promise<void> {
        try {
            await this.loginHandoffService.openLegacyInNewTab()
        } catch (error) {
            this.apiService.handleError(
                error instanceof Error
                    ? error.message
                    : "AIVA 1.0 could not be opened. Please try again."
            )
        }
    }

    logout(): void {
        this.apiService.logout(true)
    }

    changeMyPassword() {
        if (this.userService.userCanChangePassword())
            this.modalService.changePassword.next(true)
    }

    openSupportChat() {
        this.supportChatService.openChat()
    }

    shareFeedback() {
        window.open("https://airtable.com/shrrPu18emIyKfaNK", "_blank")
    }

    isStagingEnvironment() {
        return !environment.production
    }

    toggleNavbar() {
        this.designService.setSidebarCollapsed(!this.isCollapsed)
    }

    toggleUserMenu(event: MouseEvent) {
        if (this.userMenuOpen) {
            this.closeUserMenu()
            return
        }

        this.openUserMenuAt(event.currentTarget as HTMLElement)
    }

    openUserMenu(event: MouseEvent) {
        if (this.isCollapsed) {
            this.toggleNavbar()

            setTimeout(() => {
                if (this.userMenuButton != null) {
                    this.openUserMenuAt(this.userMenuButton.nativeElement)
                }
            }, 10)

            return
        }

        this.openUserMenuAt(event.currentTarget as HTMLElement)
    }

    closeUserMenu() {
        this.userMenuOpen = false
        this.userMenuOptions = undefined
    }

    private openUserMenuAt(button: HTMLElement) {
        const buttonBounds = button.getBoundingClientRect()

        this.userMenuWidth = buttonBounds.width

        this.userMenuOptions = {
            coordinates: {
                x: buttonBounds.left + 3,
                y: Math.max(8, buttonBounds.top - 120),
            },
            options: [
                {
                    data: null,
                    icon: "assets/img/bulb.svg",
                    text: "Share feedback",
                    loading: false,
                    enabled: true,
                    onClick: () => this.shareFeedback(),
                },
                {
                    data: null,
                    icon: "assets/img/menu/logout.svg",
                    text: "Logout",
                    loading: false,
                    enabled: true,
                    onClick: () => this.logout(),
                },
            ],
            onClose: () => this.closeUserMenu(),
        }

        this.userMenuOpen = true

        setTimeout(() => {
            const menuElement =
                this.userMenuComponent?.menuOptionsContainer?.nativeElement

            if (menuElement == null || this.userMenuOptions == null) {
                return
            }

            const menuHeight = menuElement.getBoundingClientRect().height
            this.userMenuOptions = {
                ...this.userMenuOptions,
                coordinates: {
                    ...this.userMenuOptions.coordinates,
                    y: Math.max(8, buttonBounds.top - menuHeight - 5),
                },
            }
        })
    }

    legacyBilling() {
        var subscription = this.billingService.subscription.getValue()

        return subscription != null && subscription.legacyPlan
    }

    getBonusDownloadsQuota() {
        return this.generalService.bonusDownloadsLeft.getValue()
    }

    getGenerationQuotaPercentage() {
        if (
            this.getDailyGenerationsUsed() === null ||
            this.getDailyGenerationsQuota() === null
        ) {
            return 0
        }

        return (
            (this.getDailyGenerationsUsed() * 100) /
            this.getDailyGenerationsQuota()
        )
    }

    openCompositionByID(type: "composition" | "training") {
        this.modalService.openCompositionModal.next(type)
    }
}
